// shared-components/context/AnimationContext.jsx
import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useWebsite } from "../hooks/useWebsite";

// Default animation settings
const defaultAnimationSettings = {
  enabled: true,
  pageTransitions: true,
  componentAnimations: true,
  reducedMotion: false,
  duration: 0.3,
  easing: "easeInOut",
  defaultPageTransition: "fade",
};

const AnimationContext = createContext(defaultAnimationSettings);

export function AnimationProvider({ children }) {
  const { websiteConfig } = useWebsite();
  const [settings, setSettings] = useState(defaultAnimationSettings);

  // Initialize animation settings from site config
  useEffect(() => {
    if (websiteConfig?.animations) {
      // Ensure all required properties are defined with proper fallbacks
      const safeAnimationConfig = {
        enabled:
          websiteConfig.animations.enabled ?? defaultAnimationSettings.enabled,
        pageTransitions:
          websiteConfig.animations.pageTransitions ??
          defaultAnimationSettings.pageTransitions,
        componentAnimations:
          websiteConfig.animations.componentAnimations ??
          defaultAnimationSettings.componentAnimations,
        reducedMotion:
          websiteConfig.animations.reducedMotion ??
          defaultAnimationSettings.reducedMotion,
        duration:
          websiteConfig.animations.duration ??
          defaultAnimationSettings.duration,
        easing:
          websiteConfig.animations.easing ?? defaultAnimationSettings.easing,
        defaultPageTransition:
          websiteConfig.animations.defaultPageTransition ??
          defaultAnimationSettings.defaultPageTransition,
      };

      setSettings({
        ...defaultAnimationSettings,
        ...safeAnimationConfig,
      });
    }
  }, [websiteConfig]);

  // Check for user's reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (e) => {
      setSettings((prevSettings) => ({
        ...prevSettings,
        reducedMotion: e.matches,
      }));
    };

    // Set initial value
    handleChange(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Toggle animation settings
  const toggleAnimations = (enabled) => {
    setSettings((prev) => ({ ...prev, enabled }));
  };

  const value = {
    ...settings,
    toggleAnimations,
  };

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
}

AnimationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAnimation = () => useContext(AnimationContext);
